/**
 * /widget/:slug — Widget compacto para link in bio, Instagram, QR, embeds.
 *
 * Diseño: fiel al diseño Stitch "Widget Compacto - Burger Bosch".
 * Checkout: usa el mismo flujo real que market.$slug (POST /api/market/:slug).
 * SSR: createServerFn con loadMarketCatalog.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createServerFn } from "@tanstack/react-start";
import {
  MapPin, MessageCircle, Clock, Plus, Minus,
  ShoppingCart, X, MoreVertical, ChevronRight,
  CheckCircle2, Scale, Search,
} from "lucide-react";
import { StructuraPlatformSignature } from "@/components/StructuraPlatformSignature";
import { formatPesos } from "@/lib/market-format";
import { filterPaymentMethodsForDelivery } from "@/lib/market-order-validate";
import {
  buildCheckoutPaymentOptions,
  isCheckoutOnlinePayment,
} from "@/lib/storefront-checkout-payments";
import {
  buildCheckoutPickupLocations,
  defaultCheckoutPickupLocationId,
  resolveCheckoutMetodEntregaConfig,
  resolveOrderPickupLocationId,
} from "@/lib/checkout-pickup";
import { readCart, writeCart } from "@/lib/market-cart-storage";
import { trackAddToCart, trackCheckoutStarted, trackRemoveFromCart } from "@/lib/storefront-funnel";
import { MarketFunnelTracker } from "@/components/market/MarketFunnelTracker";
import {
  loadMarketCatalog,
  type MarketCatalogResult,
  type MarketProduct,
} from "@/lib/market-catalog";
import { dbSmGetTenant } from "@/lib/db-service";
import { mergeTenantProductFlags } from "@/lib/tenant-product-flags";
import {
  MarketCheckoutForm,
  type MarketCheckoutFieldErrors,
} from "@/components/market/MarketCheckoutForm";
import { MarketFulfillmentStatusBanner } from "@/components/market/MarketFulfillmentStatusBanner";
import { useMarketAvailability } from "@/hooks/use-market-availability";
import { toast } from "sonner";
import { showAddedToCart } from "@/lib/market-added-to-cart";
import { DEFAULT_CHECKOUT_COUNTRY, toE164 } from "@/lib/checkout-contact";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { getRequest } from "@tanstack/react-start/server";

// ─── Loader ───────────────────────────────────────────────────────────────────

async function loadWidgetCatalogWithFlagGate(slug: string): Promise<MarketCatalogResult> {
  const result = await loadMarketCatalog(slug, { request: getRequest() });
  if (!result.ok) return result;
  const tenant = await dbSmGetTenant(result.data.tenant.id);
  const flags = mergeTenantProductFlags(tenant?.productFlags);
  if (!flags.widget_embed) {
    return {
      ok: false,
      error: "El widget embebible no está disponible para este negocio.",
      status: 403,
      code: "product_flag_locked",
    };
  }
  return result;
}

const fetchWidgetCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }) => loadWidgetCatalogWithFlagGate(slug));

export const Route = createFileRoute("/widget/$slug")({
  loader: async ({ params }) => fetchWidgetCatalog({ data: params.slug }),
  component: WidgetPage,
  head: () => ({
    meta: [{ name: "robots", content: "noindex, nofollow" }],
    links: [{
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap",
    }],
  }),
});

// ─── Types ────────────────────────────────────────────────────────────────────

type Product = MarketProduct;
interface CartItem { product: Product; qty: number }
interface BankTransferInfo {
  holder: string | null; cbu: string | null; alias: string | null;
  bank: string | null; notes: string | null;
}

function itemTotal(item: CartItem) {
  return (item.product.tipoVenta === "peso"
    ? (item.product.precioPorKg ?? 0)
    : (item.product.precioUnitario ?? 0)) * item.qty;
}
function cartTotal(cart: CartItem[]) { return cart.reduce((s, i) => s + itemTotal(i), 0); }
function cartCount(cart: CartItem[]) {
  return cart.reduce((s, i) => s + (i.product.tipoVenta === "unidad" ? i.qty : 1), 0);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function WidgetPage() {
  const { slug } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  // Carrito persistido (compartido con storefront y PDP por tenant).
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartReady, setCartReady] = useState(false);

  useEffect(() => {
    setCart(readCart(slug) as CartItem[]);
    setCartReady(true);
  }, [slug]);

  useEffect(() => {
    if (!cartReady) return;
    writeCart(slug, cart);
  }, [cart, slug, cartReady]);
  const [cartOpen, setCartOpen] = useState(false);

  // Checkout
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<"info" | "success">("info");
  const [checkoutFieldErrors, setCheckoutFieldErrors] = useState<MarketCheckoutFieldErrors>({});
  const [placing, setPlacing] = useState(false);
  const checkoutIdempotencyKeyRef = useRef<string | null>(null);
  const [orderResult, setOrderResult] = useState<{
    id: string; paymentMethod?: string; bankTransfer?: BankTransferInfo | null;
    cancelToken?: string | null; fulfillmentMode?: string; scheduledFor?: string | null;
  } | null>(null);

  const data = result.ok ? result.data : null;

  // Form inicial
  const [form, setForm] = useState(() => {
    const initial = {
      name: "", email: "", phone: "",
      phoneCountry: DEFAULT_CHECKOUT_COUNTRY,
      metodEntrega: "retiro" as "domicilio" | "retiro",
      locationId: "", direccionEntrega: "", paymentMethod: "efectivo", notes: "",
    };
    if (!data) return initial;
    const met = data.config.metodEntrega !== "ambos"
      ? (data.config.metodEntrega as "domicilio" | "retiro") : initial.metodEntrega;
    const def = data.locations.find(l => l.isDefault) ?? data.locations[0];
    const methods = data.config.paymentMethods?.length
      ? data.config.paymentMethods : ["efectivo", "transferencia"];
    return { ...initial, metodEntrega: met, locationId: def?.id ?? "", paymentMethod: methods[0] ?? "efectivo" };
  });

  const checkoutPickupLocations = useMemo(() => {
    if (!data) return [];
    return buildCheckoutPickupLocations(data.locations, data.tenant);
  }, [data]);

  const pickupAvailable = checkoutPickupLocations.length > 0;
  const deliveryConfigured = data?.config.deliveryConfigured === true;

  const checkoutMetodEntregaConfig = useMemo(() => {
    if (!data) return "ambos" as const;
    return resolveCheckoutMetodEntregaConfig(data.config.metodEntrega, pickupAvailable, deliveryConfigured);
  }, [data, pickupAvailable, deliveryConfigured]);
  const noDeliveryMethodAvailable = !pickupAvailable && !deliveryConfigured;

  useEffect(() => {
    if (!data || !pickupAvailable) {
      if (data && form.metodEntrega === "retiro" && data.config.metodEntrega === "ambos") {
        setForm((f) => ({ ...f, metodEntrega: "domicilio", locationId: "" }));
      }
      return;
    }
    if (form.metodEntrega === "retiro") {
      const valid = checkoutPickupLocations.some((l) => l.id === form.locationId);
      if (!valid) {
        setForm((f) => ({
          ...f,
          locationId: defaultCheckoutPickupLocationId(checkoutPickupLocations),
        }));
      }
    }
  }, [data, pickupAvailable, checkoutPickupLocations, form.metodEntrega, form.locationId]);

  // Payment methods filter
  const resolvedDelivery: "domicilio" | "retiro" = useMemo(() => {
    const met = checkoutMetodEntregaConfig;
    if (met === "domicilio") return "domicilio";
    if (met === "retiro") return "retiro";
    return form.metodEntrega;
  }, [checkoutMetodEntregaConfig, form.metodEntrega]);

  const paymentOptions = useMemo(() => {
    if (!data) return [];
    const bt = data.config.bankTransfer;
    return filterPaymentMethodsForDelivery(
      buildCheckoutPaymentOptions({
        tenantMethods: data.config.paymentMethods ?? [],
        mpEnabled: data.config.mpEnabled,
        bankTransferConfigured: Boolean(bt?.cbu || bt?.alias || bt?.holder),
        onlinePaymentMethodIds: data.config.onlinePaymentMethodIds ?? [],
      }),
      resolvedDelivery,
    );
  }, [data, resolvedDelivery]);

  useEffect(() => {
    if (!paymentOptions.length) return;
    if (!paymentOptions.includes(form.paymentMethod))
      setForm(f => ({ ...f, paymentMethod: paymentOptions[0] }));
  }, [paymentOptions, form.paymentMethod]);

  useEffect(() => {
    if (!data) return;
    const bt = data.config.bankTransfer;
    const allowed = filterPaymentMethodsForDelivery(
      buildCheckoutPaymentOptions({
        tenantMethods: data.config.paymentMethods ?? [],
        mpEnabled: data.config.mpEnabled,
        bankTransferConfigured: Boolean(bt?.cbu || bt?.alias || bt?.holder),
        onlinePaymentMethodIds: data.config.onlinePaymentMethodIds ?? [],
      }),
      form.metodEntrega,
    );
    if (!allowed.includes(form.paymentMethod) && allowed[0]) {
      setForm((f) => ({ ...f, paymentMethod: allowed[0] }));
    }
  }, [form.metodEntrega, data, form.paymentMethod]);

  const cartPrepMinutes = useMemo(() =>
    cart.reduce((max, line) => {
      const p = data?.products.find(x => x.id === line.product.id);
      return Math.max(max, p?.tiempoPreparacionMinutos ?? 0);
    }, 0), [cart, data?.products]);

  const availabilityLocationId = useMemo(() => {
    if (!data) return "";
    if (form.metodEntrega === "retiro") {
      return resolveOrderPickupLocationId(form.locationId) ?? "";
    }
    const def =
      checkoutPickupLocations.find((l) => !l.isBusinessFallback) ?? checkoutPickupLocations[0];
    return resolveOrderPickupLocationId(def?.id) ?? "";
  }, [data, form.metodEntrega, form.locationId, checkoutPickupLocations]);

  const { evaluation: fulfillmentEval, loading: fulfillmentLoading, error: fulfillmentError } =
    useMarketAvailability({
      slug, metodEntrega: form.metodEntrega, locationId: availabilityLocationId,
      cartPrepMinutes, enabled: checkoutOpen && checkoutStep === "info",
    });

  const checkoutBlocked = fulfillmentEval?.enabled === true && fulfillmentEval.canPlaceOrder === false;

  // Cart ops
  const addToCart = useCallback((product: Product, qty = 1) => {
    setCart(prev => {
      const ex = prev.find(i => i.product.id === product.id);
      if (ex) return prev.map(i => i.product.id === product.id ? { ...i, qty: i.qty + qty } : i);
      return [...prev, { product, qty }];
    });
    trackAddToCart(slug, { id: product.id, name: product.name, slug: product.slug }, qty, { channel: "widget" });
    showAddedToCart({
      title: product.name,
      storeSlug: slug,
      imageUrl: product.imagenUrl,
      qty,
      onBuy: () => {
        setCheckoutOpen(true);
        setCheckoutStep("info");
      },
    });
  }, [slug]);

  const updateQty = useCallback((productId: string, delta: number) => {
    setCart(prev => {
      if (delta < 0) {
        const line = prev.find(i => i.product.id === productId);
        if (line) {
          trackRemoveFromCart(
            slug,
            { id: line.product.id, name: line.product.name, slug: line.product.slug },
            Math.min(line.qty, Math.abs(delta)),
            { channel: "widget" },
          );
        }
      }
      return prev.map(i => i.product.id === productId ? { ...i, qty: Math.max(0, i.qty + delta) } : i)
        .filter(i => i.qty > 0);
    });
  }, [slug]);

  const qtyOf = (id: string) => cart.find(i => i.product.id === id)?.qty ?? 0;

  // Checkout submit — idéntico al storefront
  const handlePlaceOrder = async () => {
    if (!data || placing) return;
    setCheckoutFieldErrors({});
    if (!form.name.trim() || !form.email.trim()) {
      const msg = "Nombre y email son requeridos";
      setCheckoutFieldErrors({ name: msg, email: msg });
      toast.error(msg); return;
    }
    if (form.metodEntrega === "domicilio" && !form.direccionEntrega.trim()) {
      const msg = "Ingresá tu dirección de entrega";
      setCheckoutFieldErrors({ direccionEntrega: msg });
      toast.error(msg); return;
    }
    if (!paymentOptions.length) {
      toast.error("No hay métodos de pago disponibles."); return;
    }
    if (checkoutBlocked) {
      toast.error(fulfillmentEval?.message ?? "No podemos tomar este pedido en este horario."); return;
    }
    setPlacing(true);
    try {
      if (!checkoutIdempotencyKeyRef.current) {
        checkoutIdempotencyKeyRef.current = crypto.randomUUID();
      }
      const idempotencyKey = checkoutIdempotencyKeyRef.current;
      const resp = await fetch(`/api/market/${slug}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify({
          customerName: form.name.trim(),
          customerEmail: form.email.trim(),
          customerPhone:
            toE164(form.phone, form.phoneCountry) ?? (form.phone.trim() || undefined),
          metodEntrega: form.metodEntrega,
          locationId:
            form.metodEntrega === "retiro"
              ? resolveOrderPickupLocationId(form.locationId)
              : undefined,
          direccionEntrega: form.metodEntrega === "domicilio" ? form.direccionEntrega.trim() : undefined,
          paymentMethod: form.paymentMethod,
          scheduledFor: fulfillmentEval?.scheduledForIso ?? null,
          notes: form.notes.trim() || undefined,
          idempotencyKey,
          items: cart.map(i => ({
            productId: i.product.id, productName: i.product.name,
            tipoVenta: i.product.tipoVenta, cantidad: i.qty, unidadMedida: i.product.unidadMedida,
            precioUnitario: i.product.tipoVenta === "unidad" ? i.product.precioUnitario ?? undefined : undefined,
            precioPorKg: i.product.tipoVenta === "peso" ? i.product.precioPorKg ?? undefined : undefined,
          })),
        }),
      });
      const r = await resp.json();
      if (!resp.ok) throw new Error(r.error ?? "Error al crear pedido");
      const resolvedPayment = String(r.paymentMethod ?? form.paymentMethod ?? "");
      if (r.paymentLink && isCheckoutOnlinePayment(resolvedPayment)) {
        window.location.href = r.paymentLink; return;
      }
      setOrderResult({
        id: r.id, paymentMethod: r.paymentMethod ?? form.paymentMethod,
        bankTransfer: r.bankTransfer ?? null, cancelToken: r.cancelToken ?? null,
        fulfillmentMode: r.fulfillmentMode ?? "immediate", scheduledFor: r.scheduledFor ?? null,
      });
      setCheckoutStep("success");
      setCart([]);
      checkoutIdempotencyKeyRef.current = null;
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al procesar el pedido");
    } finally {
      setPlacing(false);
    }
  };

  const offlineScreen = renderMarketPublicOffline(result);
  if (offlineScreen) return offlineScreen;

  // ── Error ─────────────────────────────────────────────────────────────────
  if (!result.ok || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-6 text-center font-[Inter,sans-serif]">
        <div>
          <p className="text-3xl">🍽️</p>
          <p className="mt-2 font-semibold text-gray-800">Tienda no disponible</p>
          <p className="mt-1 text-sm text-gray-400">{!result.ok ? result.error : "Esta tienda no existe."}</p>
        </div>
      </div>
    );
  }

  const { tenant, config, locations } = data;
  const accent = config.primaryColor ?? "#2563eb";
  const mainLocation = locations.find(l => l.isDefault) ?? locations[0];
  const totalCart = cartTotal(cart);
  const countCart = cartCount(cart);

  // ── Search + category filter state ──
  const [searchQ, setSearchQ] = useState("");
  const [activeCat, setActiveCat] = useState<string | null>(null);

  // Categorías únicas con productos
  const categories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of data.products) {
      if (p.categoriaId && p.categoriaName && !seen.has(p.categoriaId)) {
        seen.set(p.categoriaId, p.categoriaName);
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [data.products]);

  // Productos filtrados
  const filteredProducts = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return data.products.filter(p => {
      const matchCat = !activeCat || p.categoriaId === activeCat;
      const matchQ = !q || p.name.toLowerCase().includes(q) ||
        (p.description ?? "").toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q));
      return matchCat && matchQ;
    });
  }, [data.products, activeCat, searchQ]);

  const popular = filteredProducts.filter(p =>
    p.tags.includes("Lo más pedido") || p.tags.includes("Destacado")).slice(0, 6);
  const rest = filteredProducts.filter(p =>
    !p.tags.includes("Lo más pedido") && !p.tags.includes("Destacado"));
  const showRest = rest.length > 0 ? rest : (popular.length === 0 ? filteredProducts : []);
  const isFiltering = searchQ.trim().length > 0 || activeCat !== null;

  return (
    <div className="mx-auto min-h-screen max-w-sm bg-white font-[Inter,sans-serif]">
      <MarketFunnelTracker slug={slug} channel="widget" />

      {/* ── Header ── */}
      <header className="sticky top-0 z-30 border-b border-gray-100 bg-white">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            {tenant.logoUrl ? (
              <img src={tenant.logoUrl} alt={tenant.name}
                className="h-8 w-8 rounded-lg object-cover ring-1 ring-gray-200" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg text-sm"
                style={{ background: accent, color: "#fff", fontWeight: 700 }}>
                {tenant.name.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-sm font-bold text-gray-900">{tenant.name}</span>
          </div>

          <div className="flex items-center gap-2">
            {(mainLocation?.address || tenant.address) && (
              <a
                href={mainLocation?.mapsUrl ?? `https://maps.google.com/?q=${encodeURIComponent(mainLocation?.address ?? tenant.address ?? "")}`}
                target="_blank" rel="noopener noreferrer"
                className="hidden items-center gap-1 rounded-full border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 sm:flex">
                <MapPin className="h-3 w-3" /> Ver local
              </a>
            )}
            {config.whatsappNumber && (
              <a href={`https://wa.me/${config.whatsappNumber.replace(/\D/g, "")}`}
                target="_blank" rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-[#25d366] hover:bg-gray-50">
                <MessageCircle className="h-4 w-4" />
              </a>
            )}
            {countCart > 0 ? (
              <button type="button" onClick={() => setCartOpen(true)}
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white active:scale-95 transition-transform"
                style={{ backgroundColor: accent }}>
                <ShoppingCart className="h-3.5 w-3.5" />
                {countCart}
              </button>
            ) : (
              <button type="button" className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100">
                <MoreVertical className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Search bar */}
        <div className="px-4 pb-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Buscar productos..."
              className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-8 pr-8 text-sm outline-none focus:border-gray-400 focus:bg-white"
            />
            {searchQ && (
              <button type="button" onClick={() => setSearchQ("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Category pills */}
        {categories.length > 0 && (
          <div className="flex gap-2 overflow-x-auto px-4 pb-3 scrollbar-none" style={{ scrollbarWidth: "none" }}>
            <button
              type="button"
              onClick={() => setActiveCat(null)}
              className="shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition-colors"
              style={!activeCat
                ? { backgroundColor: accent, color: "#fff" }
                : { backgroundColor: "#f3f4f6", color: "#374151" }}
            >
              Todo
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCat(activeCat === cat.id ? null : cat.id)}
                className="shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition-colors"
                style={activeCat === cat.id
                  ? { backgroundColor: accent, color: "#fff" }
                  : { backgroundColor: "#f3f4f6", color: "#374151" }}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="px-4 pb-10">

        {/* Info rápida si no está filtrando */}
        {!isFiltering && (mainLocation?.hours || mainLocation?.address) && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
            {mainLocation?.hours && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Clock className="h-3 w-3" /> {mainLocation.hours}
              </span>
            )}
            {mainLocation?.address && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <MapPin className="h-3 w-3" /> {mainLocation.address}
              </span>
            )}
          </div>
        )}

        {/* Sin resultados */}
        {filteredProducts.length === 0 && (
          <div className="flex flex-col items-center py-16 text-center">
            <Search className="h-8 w-8 text-gray-200" />
            <p className="mt-2 text-sm text-gray-400">Sin resultados para "{searchQ}"</p>
            <button type="button" onClick={() => { setSearchQ(""); setActiveCat(null); }}
              className="mt-3 text-xs font-semibold" style={{ color: accent }}>
              Ver todo el catálogo
            </button>
          </div>
        )}

        {/* ── Lo más pedido (solo si no filtra por texto) ── */}
        {popular.length > 0 && !searchQ && (
          <section className="mt-5">
            <p className="mb-2.5 text-xs font-bold uppercase tracking-widest text-gray-400">
              {activeCat ? categories.find(c => c.id === activeCat)?.name : "Lo más pedido"}
            </p>
            <div className="overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
              {popular.map((p, i) => (
                <div key={p.id}
                  className={`flex items-center gap-3 bg-white px-3 py-2.5 ${i < popular.length - 1 ? "border-b border-gray-100" : ""}`}>
                  <a href={`/market/${slug}/p/${p.slug}`} className="shrink-0">
                    {p.imagenUrl ? (
                      <img src={p.imagenUrl} alt={p.name}
                        className="h-12 w-12 rounded-xl object-cover" loading="lazy" />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 text-xl">🛒</div>
                    )}
                  </a>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <a href={`/market/${slug}/p/${p.slug}`}
                      className="line-clamp-1 text-sm font-semibold text-gray-900">{p.name}</a>
                    <span className="text-sm font-bold" style={{ color: accent }}>
                      {formatPesos(p.precioUnitario ?? p.precioPorKg ?? 0)}
                      {p.tipoVenta === "peso" && <span className="ml-0.5 text-xs font-normal text-gray-400">/kg</span>}
                    </span>
                  </div>
                  <WidgetQtyControl
                    qty={qtyOf(p.id)} tipoVenta={p.tipoVenta} accent={accent}
                    onAdd={() => addToCart(p)}
                    onInc={() => updateQty(p.id, p.tipoVenta === "peso" ? 0.1 : 1)}
                    onDec={() => updateQty(p.id, p.tipoVenta === "peso" ? -0.1 : -1)}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Grid de productos (resto / búsqueda) ── */}
        {(showRest.length > 0 || (searchQ && filteredProducts.length > 0)) && (
          <section className="mt-5">
            {!searchQ && showRest.length > 0 && (
              <p className="mb-2.5 text-xs font-bold uppercase tracking-widest text-gray-400">
                {activeCat && popular.length === 0 ? categories.find(c => c.id === activeCat)?.name : "Más productos"}
              </p>
            )}
            {searchQ && (
              <p className="mb-2.5 text-xs font-bold uppercase tracking-widest text-gray-400">
                {filteredProducts.length} resultado{filteredProducts.length !== 1 ? "s" : ""}
              </p>
            )}
            <div className="grid grid-cols-2 gap-2.5">
              {(searchQ ? filteredProducts : showRest).map(p => (
                <div key={p.id} className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                  <a href={`/market/${slug}/p/${p.slug}`} className="block">
                    {p.imagenUrl ? (
                      <img src={p.imagenUrl} alt={p.name}
                        className="aspect-square w-full object-cover" loading="lazy" />
                    ) : (
                      <div className="flex aspect-square w-full items-center justify-center bg-gray-50 text-3xl">🛒</div>
                    )}
                  </a>
                  <div className="p-2.5">
                    <p className="line-clamp-2 text-xs font-semibold leading-tight text-gray-900">{p.name}</p>
                    <p className="mt-1 text-sm font-bold" style={{ color: accent }}>
                      {formatPesos(p.precioUnitario ?? p.precioPorKg ?? 0)}
                      {p.tipoVenta === "peso" && <span className="ml-0.5 text-[10px] font-normal text-gray-400">/kg</span>}
                    </p>
                    <div className="mt-2">
                      {qtyOf(p.id) > 0 ? (
                        <div className="flex items-center justify-between rounded-xl bg-gray-100 px-2 py-1">
                          <button type="button"
                            onClick={() => updateQty(p.id, p.tipoVenta === "peso" ? -0.1 : -1)}
                            className="flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm active:scale-90">
                            <Minus className="h-3 w-3 text-gray-600" />
                          </button>
                          <span className="text-xs font-bold tabular-nums" style={{ color: accent }}>
                            {p.tipoVenta === "peso" ? qtyOf(p.id).toFixed(1) : qtyOf(p.id)}
                          </span>
                          <button type="button"
                            onClick={() => updateQty(p.id, p.tipoVenta === "peso" ? 0.1 : 1)}
                            className="flex h-6 w-6 items-center justify-center rounded-full text-white shadow-sm active:scale-90"
                            style={{ backgroundColor: accent }}>
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => addToCart(p)}
                          className="flex w-full items-center justify-center gap-1 rounded-xl py-1.5 text-xs font-bold text-white transition-all active:scale-[0.97]"
                          style={{ backgroundColor: accent }}>
                          <Plus className="h-3.5 w-3.5" /> Agregar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <StructuraPlatformSignature className="mt-10 text-gray-300" variant="on-dark" />
      </div>

      {/* ── Cart drawer ── */}
      {cartOpen && (
        <div className="fixed inset-0 z-40 flex flex-col bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-gray-700" />
              <span className="font-bold text-gray-900">
                Carrito · {countCart} ítem{countCart !== 1 ? "s" : ""}
              </span>
            </div>
            <button type="button" onClick={() => setCartOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <ShoppingCart className="h-10 w-10 text-gray-200" />
                <p className="mt-3 text-sm text-gray-400">Tu carrito está vacío</p>
                <button type="button" onClick={() => setCartOpen(false)}
                  className="mt-4 rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-500">
                  Seguir eligiendo
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {cart.map(item => (
                  <div key={item.product.id} className="flex items-center gap-3">
                    {item.product.imagenUrl && (
                      <img src={item.product.imagenUrl} alt={item.product.name}
                        className="h-14 w-14 rounded-xl object-cover shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{item.product.name}</p>
                      <p className="text-sm font-bold" style={{ color: accent }}>
                        {formatPesos(itemTotal(item))}
                      </p>
                      {item.product.tipoVenta === "peso" && (
                        <p className="text-xs text-orange-500 flex items-center gap-1">
                          <Scale className="h-3 w-3" /> Precio estimado
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button"
                        onClick={() => updateQty(item.product.id, item.product.tipoVenta === "peso" ? -0.1 : -1)}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100">
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm font-bold tabular-nums">
                        {item.product.tipoVenta === "peso" ? item.qty.toFixed(1) : item.qty}
                      </span>
                      <button type="button"
                        onClick={() => updateQty(item.product.id, item.product.tipoVenta === "peso" ? 0.1 : 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-900 text-white">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <div className="border-t border-gray-100 px-4 py-4 space-y-3">
              <div className="flex justify-between text-sm font-medium">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-bold text-gray-900 tabular-nums">{formatPesos(totalCart)}</span>
              </div>
              <button type="button"
                onClick={() => {
                  setCartOpen(false);
                  setCheckoutOpen(true);
                  setCheckoutStep("info");
                  trackCheckoutStarted(slug, {
                    channel: "widget",
                    itemCount: cart.reduce((s, l) => s + l.qty, 0),
                  });
                }}
                className="flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-sm font-bold text-white transition-all active:scale-[0.98]"
                style={{ backgroundColor: accent }}>
                Hacer pedido <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Checkout dialog — idéntico al storefront ── */}
      <Dialog open={checkoutOpen} onOpenChange={open => {
        if (!open && checkoutStep === "success") { setCheckoutOpen(false); setCheckoutStep("info"); }
        else if (!placing) setCheckoutOpen(open);
      }}>
        <DialogContent className="flex max-h-[min(92dvh,100%)] w-[calc(100%-1rem)] max-w-lg flex-col overflow-hidden p-0">
          <DialogDescription className="sr-only">Panel de checkout del widget.</DialogDescription>
          {checkoutStep === "success" ? (
            <div className="overflow-y-auto p-6">
              <div className="flex flex-col items-center gap-4 py-4 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle2 className="h-9 w-9 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {orderResult?.paymentMethod === "transferencia" ? "Pedido registrado" : "¡Pedido confirmado!"}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Pedido #{orderResult?.id.slice(-6).toUpperCase()}
                  </p>
                </div>
                {orderResult?.paymentMethod === "transferencia" && data.config.bankTransfer?.cbu && (
                  <div className="w-full rounded-xl border border-gray-100 bg-gray-50 p-4 text-left text-sm space-y-1">
                    {data.config.bankTransfer.holder && <p><span className="text-gray-500">Titular:</span> {data.config.bankTransfer.holder}</p>}
                    {data.config.bankTransfer.cbu && <p><span className="text-gray-500">CBU:</span> {data.config.bankTransfer.cbu}</p>}
                    {data.config.bankTransfer.alias && <p><span className="text-gray-500">Alias:</span> {data.config.bankTransfer.alias}</p>}
                  </div>
                )}
                <button type="button" onClick={() => { setCheckoutOpen(false); setCheckoutStep("info"); }}
                  className="w-full rounded-2xl py-3.5 text-sm font-bold text-white"
                  style={{ backgroundColor: accent }}>
                  Seguir comprando
                </button>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader className="shrink-0 border-b px-4 py-4">
                <DialogTitle className="text-lg font-bold">Finalizar pedido</DialogTitle>
              </DialogHeader>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                <MarketCheckoutForm
                  form={form}
                  setForm={updater => { setCheckoutFieldErrors({}); setForm(updater); }}
                  cart={cart}
                  cartTotal={totalCart}
                  itemCount={countCart}
                  metodEntregaConfig={checkoutMetodEntregaConfig}
                  noDeliveryMethodAvailable={noDeliveryMethodAvailable}
                  locations={checkoutPickupLocations}
                  paymentOptions={paymentOptions}
                  bankTransfer={config.bankTransfer}
                  placing={placing}
                  fieldErrors={checkoutFieldErrors}
                  fulfillmentBanner={
                    <MarketFulfillmentStatusBanner
                      evaluation={fulfillmentEval}
                      loading={fulfillmentLoading}
                      error={fulfillmentError}
                    />
                  }
                  checkoutBlocked={checkoutBlocked}
                  onSubmit={handlePlaceOrder}
                  onCancel={() => setCheckoutOpen(false)}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Add/qty control ──────────────────────────────────────────────────────────

function WidgetQtyControl({
  qty, tipoVenta, accent, onAdd, onInc, onDec,
}: {
  qty: number; tipoVenta: "unidad" | "peso"; accent: string;
  onAdd: () => void; onInc: () => void; onDec: () => void;
}) {
  if (qty === 0) {
    return (
      <button type="button" onClick={onAdd}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white transition-all active:scale-90">
        <Plus className="h-4 w-4" />
      </button>
    );
  }
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <button type="button" onClick={onDec}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 transition-all active:scale-90">
        <Minus className="h-3.5 w-3.5 text-gray-700" />
      </button>
      <span className="w-6 text-center text-sm font-bold tabular-nums" style={{ color: accent }}>
        {tipoVenta === "peso" ? qty.toFixed(1) : qty}
      </span>
      <button type="button" onClick={onInc}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-900 text-white transition-all active:scale-90">
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
